import { Link } from "react-router-dom";
import { Heart, HardHat, Compass } from "lucide-react";
import { PageHero, Reveal } from "@/components/site/Reveal";
import { CtaButtons } from "@/components/site/Seo";
import { COMPANY } from "@/data/site";
import usePageMeta from "@/hooks/usePageMeta";

export default function Presentation() {
  usePageMeta("À propos – Conciergerie Familly 29 à Brest", "Conciergerie Familly 29, conciergerie de proximité à Brest : surveillance, remise de clés, entretien et petits travaux, avec l'expérience de la maintenance du bâtiment.");
  return (
    <>
      <PageHero testid="presentation-hero" eyebrow="À propos" title="Une conciergerie de proximité à Brest" image="/images/port.jpg" />
      <section className="mx-auto grid max-w-7xl gap-14 px-5 py-24 md:grid-cols-12 lg:px-8">
        <Reveal className="md:col-span-7">
          <p className="text-lg leading-relaxed text-slate-700 md:text-xl">
            Basée à Brest, <strong className="text-[#0F2840]">Conciergerie Familly 29</strong> propose des services de proximité
            pour l'entretien et la surveillance des logements : visites de contrôle, remise de clés, entretien courant,
            petits travaux et coordination d'artisans.
          </p>
          <p className="mt-6 leading-relaxed text-slate-600">
            Nous accompagnons les propriétaires absents, les militaires en mission ou en mutation et les résidences secondaires.
            Grâce à notre expérience dans la maintenance du bâtiment, nous associons proximité, suivi personnalisé et regard technique.
          </p>
          <h2 className="mt-10 font-display text-2xl font-semibold">Notre cadre d'intervention</h2>
          <p className="mt-3 leading-relaxed text-slate-600">
            Nous réalisons une mission matérielle d'intendance. Nous ne sommes pas une agence immobilière : nous ne gérons pas de location,
            n'encaissons pas de loyers et ne réalisons pas de travaux réglementés. Entreprise individuelle d'{COMPANY.dirigeant}, SIREN {COMPANY.siren}.
          </p>
          <div className="mt-10"><CtaButtons id="apropos" /></div>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link to="/prestations" className="text-sm font-bold text-[#D82C2C] underline" data-testid="presentation-prestations-btn">Découvrir nos services</Link>
            <Link to="/contact" className="text-sm font-bold text-[#0F2840] underline" data-testid="presentation-contact-btn">Nous contacter</Link>
          </div>
        </Reveal>
        <Reveal className="md:col-span-5" delay={0.1}>
          <div className="relative">
            <img src="/images/maison.jpg" alt="Maison entretenue par la Conciergerie Familly 29 à Brest" loading="lazy" className="h-[420px] w-full rounded-lg object-cover" />
          </div>
        </Reveal>
      </section>
      <section className="bg-[#0F2840] py-24 text-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 md:grid-cols-3 lg:px-8">
          {[
            { i: Compass, t: "Proximité", d: "Une présence locale à Brest et dans ses environs, pour un suivi personnalisé." },
            { i: HardHat, t: "Regard technique", d: "L'expérience de la maintenance du bâtiment pour repérer ce qui mérite votre attention." },
            { i: Heart, t: "Esprit familial", d: "Nous prenons soin de votre logement comme s'il s'agissait du nôtre." },
          ].map((v, k) => (
            <Reveal key={v.t} delay={k * 0.1}>
              <div className="border-l-2 border-[#D82C2C] pl-6">
                <v.i className="h-7 w-7 text-[#ff8080]" />
                <h2 className="mt-4 font-display text-2xl">{v.t}</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/75">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
